'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @primitive DragAnnouncer
 * ROLE: live-region announcer for drag-and-drop operations
 * ARIA: aria-live="assertive" aria-atomic="true" (hidden visually, visible to AT)
 * KEYBOARD: driven by DnD events, not interactive itself
 * FOCUS: none (invisible to sighted users)
 * LIVE-REGION: assertive — announces drag state changes to screen readers
 * RSC: CLIENT
 * WAI-APG: https://www.w3.org/WAI/ARIA/apg/patterns/listbox/#wai-aria-roles-states-and-properties-12
 */
import { useEffect, useRef, useCallback } from 'react';
import { useDragDropMonitor } from '@dnd-kit/react';
/**
 * Invisible live-region that announces drag state changes to assistive technology.
 * Render once inside `<DragDropProvider>` alongside your sortable list.
 *
 * Usage:
 * ```tsx
 * <DragAnnouncer
 *   total={items.length}
 *   srStrings={{
 *     onPickUp: (item) => `Picked up ${item}. Use arrow keys to move; Space or Enter to drop; Escape to cancel.`,
 *     onMove: (item, pos, total) => `${item} is over position ${pos} of ${total}.`,
 *     onDrop: (item, pos) => `${item} was dropped at position ${pos}.`,
 *     onCancel: (item) => `Drag cancelled. ${item} returned to original position.`,
 *   }}
 * />
 * ```
 */
export function DragAnnouncer({ srStrings, total }) {
    const liveRef = useRef(null);
    const moveTimerRef = useRef(null);
    const announce = useCallback((message) => {
        const el = liveRef.current;
        if (!el)
            return;
        // Clear + re-set forces the live region to re-announce even identical messages.
        el.textContent = '';
        // Micro-task flush so the DOM sees the empty state before the new text.
        requestAnimationFrame(() => {
            el.textContent = message;
        });
    }, []);
    useDragDropMonitor({
        onDragStart(event) {
            const label = event.operation.source?.data?.label ?? String(event.operation.source?.id ?? 'item');
            announce(srStrings.onPickUp(label));
        },
        onDragOver(event) {
            if (moveTimerRef.current !== null)
                clearTimeout(moveTimerRef.current);
            moveTimerRef.current = setTimeout(() => {
                const label = event.operation.source?.data?.label ?? String(event.operation.source?.id ?? 'item');
                // `index` on the target sortable is 0-based; surface 1-based to humans.
                const rawIndex = event.operation.target?.index;
                const position = rawIndex !== undefined ? rawIndex + 1 : 1;
                announce(srStrings.onMove(label, position, total));
            }, 250);
        },
        onDragEnd(event) {
            if (moveTimerRef.current !== null) {
                clearTimeout(moveTimerRef.current);
                moveTimerRef.current = null;
            }
            const label = event.operation.source?.data?.label ?? String(event.operation.source?.id ?? 'item');
            if (event.canceled) {
                announce(srStrings.onCancel(label));
            }
            else {
                const rawIndex = event.operation.target?.index;
                const position = rawIndex !== undefined ? rawIndex + 1 : 1;
                announce(srStrings.onDrop(label, position));
            }
        },
    });
    // Cleanup debounce timer on unmount.
    useEffect(() => {
        return () => {
            if (moveTimerRef.current !== null)
                clearTimeout(moveTimerRef.current);
        };
    }, []);
    return (_jsx("div", { ref: liveRef, role: "status", "aria-live": "assertive", "aria-atomic": "true", style: {
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            borderWidth: 0,
        } }));
}
//# sourceMappingURL=DragAnnouncer.js.map